import { DetailCard } from '@trellis/roots/data'
import type {
  DocumentDiscountType,
  InvoiceTaxType,
  TagResponseDto,
} from '@trellis/shared'
import { FEATURE, TAX_TYPE_LABELS } from '@trellis/shared'
import { FileText, User } from 'lucide-react'

import { ClientPreviewTrigger } from '@/components/client-preview-sheet'
import { TagsEditorCard } from '@/components/tags-editor-card'
import {
  useAssignInvoiceTagsMutation,
  useCreateTagMutation,
  useRemoveInvoiceTagMutation,
  useTagsQuery,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { getCurrencyFormatter } from '@/lib/format'

interface InvoiceSidebarProps {
  id?: string
  clientId?: string
  clientName?: string
  clientEmail?: string | null
  clientPhone?: string | null
  clientCompany?: string | null
  clientAddress?: string | null
  subtotal: number
  discountType: DocumentDiscountType
  discount: number
  discountAmount: number
  taxAmount: number
  total: number
  currency: string
  taxType: InvoiceTaxType
  taxRate: number
  tags?: TagResponseDto[]
  onPreviewClient?: (clientId: string) => void
}

export function InvoiceSidebar({
  id,
  clientId,
  clientName,
  clientEmail,
  clientPhone,
  clientCompany,
  clientAddress,
  subtotal,
  discountType,
  discount,
  discountAmount,
  taxAmount,
  total,
  currency,
  taxType,
  taxRate,
  tags = [],
  onPreviewClient,
}: InvoiceSidebarProps) {
  const currencyFormatter = getCurrencyFormatter(currency)

  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const allTags = useTagsQuery()
  const assignTagsMutation = useAssignInvoiceTagsMutation()
  const removeTagMutation = useRemoveInvoiceTagMutation()
  const createTagMutation = useCreateTagMutation()

  return (
    <div className="space-y-4">
      <DetailCard
        title="Invoicing to"
        icon={User}
        footer={
          clientId &&
          onPreviewClient && (
            <div className="flex justify-end">
              <ClientPreviewTrigger clientId={clientId} onPreview={onPreviewClient} />
            </div>
          )
        }
      >
        {clientName ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">{clientName}</p>
            {clientCompany && (
              <p className="text-muted-foreground text-xs">{clientCompany}</p>
            )}
            {clientEmail && (
              <p className="text-muted-foreground text-xs">{clientEmail}</p>
            )}
            {clientPhone && (
              <p className="text-muted-foreground text-xs">{clientPhone}</p>
            )}
            {clientAddress && (
              <p className="text-muted-foreground text-xs whitespace-pre-line">
                {clientAddress}
              </p>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No client selected</p>
        )}
      </DetailCard>

      <DetailCard title="Total due" icon={FileText}>
        <div
          className="tabular-nums"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 900,
            fontSize: 32,
            letterSpacing: '-0.02em',
            lineHeight: 1,
          }}
        >
          {currencyFormatter.format(total)}
        </div>
        <div className="mt-3 space-y-1 text-sm">
          <div className="text-muted-foreground flex justify-between">
            <span>Subtotal</span>
            <span className="text-foreground tabular-nums">
              {currencyFormatter.format(subtotal)}
            </span>
          </div>
          {discountType !== 'NONE' && discountAmount > 0 && (
            <div className="text-muted-foreground flex justify-between">
              <span>
                Discount{discountType === 'PERCENTAGE' ? ` (${discount}%)` : ''}
              </span>
              <span className="text-foreground tabular-nums">
                -{currencyFormatter.format(discountAmount)}
              </span>
            </div>
          )}
          {taxType !== 'NONE' && (
            <div className="text-muted-foreground flex justify-between">
              <span>
                {TAX_TYPE_LABELS[taxType]} ({(taxRate * 100).toFixed(0)}%)
              </span>
              <span className="text-foreground tabular-nums">
                {currencyFormatter.format(taxAmount)}
              </span>
            </div>
          )}
        </div>
        <p className="text-muted-foreground mt-2 text-right text-xs">
          {currency}
          {taxType !== 'NONE' ? ` · ${taxType} inclusive` : ''}
        </p>
      </DetailCard>

      {isTagsEnabled && (
        <TagsEditorCard
          tags={tags}
          allTags={allTags.data ?? []}
          onAssign={(tagIds) =>
            assignTagsMutation.mutateAsync({ invoiceId: id ?? '', tagIds })
          }
          onRemove={(tagId) =>
            removeTagMutation.mutateAsync({ invoiceId: id ?? '', tagId })
          }
          onCreateTag={async (name, color) => {
            const result = await createTagMutation.mutateAsync({
              name,
              ...(color ? { color } : {}),
            })
            return result.data
          }}
        />
      )}
    </div>
  )
}
